import { cn } from "@/lib/utils";

/**
 * A few Lucide icons as plain elements for rows that repeat dozens of times.
 * lucide-react wraps every icon in a component with a context read, so a table
 * with four icons per row hydrates four extra fibers per row for markup that
 * never changes. These return the element lucide-react would render, attribute
 * for attribute (geometry copied from Lucide, ISC), and no fiber of their own.
 * `__tests__/static-lucide.test.tsx` fails when lucide-react renders an icon
 * differently, so an update cannot leave a copy behind unnoticed.
 */
const svgAttributes = {
  xmlns: "http://www.w3.org/2000/svg",
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

const icon = (
  name: string,
  className: string,
  shapes: React.ReactNode,
  aliases: readonly string[] = [],
) => {
  // lucide-react's own hook classes, one for the icon's name and one for each
  // alias. They are not Tailwind utilities; kept so the element matches the
  // component's output attribute for attribute.
  const hookClasses = [name, ...aliases].map((hook) => `lucide-${hook}`);
  return (
    <svg
      {...svgAttributes}
      // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- "lucide" is a hook class too, see hookClasses
      className={cn("lucide", hookClasses, className)}
      aria-hidden="true"
    >
      {shapes}
    </svg>
  );
};

export const starIcon = (className: string) =>
  icon(
    "star",
    className,
    <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />,
  );

export const gitCommitHorizontalIcon = (className: string) =>
  icon(
    "git-commit-horizontal",
    className,
    <>
      <circle cx="12" cy="12" r="3" />
      <line x1="3" x2="9" y1="12" y2="12" />
      <line x1="15" x2="21" y1="12" y2="12" />
    </>,
    ["git-commit"],
  );

export const chevronRightIcon = (className: string) =>
  icon("chevron-right", className, <path d="m9 18 6-6-6-6" />);

export const chevronDownIcon = (className: string) =>
  icon("chevron-down", className, <path d="m6 9 6 6 6-6" />);

export const presentationIcon = (className: string) =>
  icon(
    "presentation",
    className,
    <>
      <path d="M2 3h20" />
      <path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" />
      <path d="m7 21 5-5 5 5" />
    </>,
  );

export const fileCodeIcon = (className: string) =>
  icon(
    "file-code",
    className,
    <>
      <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
      <path d="M14 2v5a1 1 0 0 0 1 1h5" />
      <path d="M10 12.5 8 15l2 2.5" />
      <path d="m14 12.5 2 2.5-2 2.5" />
    </>,
  );

export const videoIcon = (className: string) =>
  icon(
    "video",
    className,
    <>
      <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </>,
  );

export const externalLinkIcon = (className: string) =>
  icon(
    "external-link",
    className,
    <>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </>,
  );
